import AuthService from '~/services/auth.service';

import { DRAFT_TTL_MS } from './constants';
import {
  type BugReportFormValues,
  zodBugReportDraftSchema,
} from './validations';

function draftKey(): string {
  const userId = AuthService.getUserIdFromAccessToken() ?? 'anonymous';

  return `bug-report-draft:${userId}`;
}

/**
 * Loads the draft for the current user.
 * Returns null if no draft exists, if it is expired, or if it is malformed.
 */
export function loadDraft(): BugReportFormValues | null {
  try {
    const raw = globalThis.localStorage.getItem(draftKey());

    if (!raw) {
      return null;
    }

    const parsed = zodBugReportDraftSchema.safeParse(JSON.parse(raw));

    if (!parsed.success) {
      clearDraft();
      return null;
    }

    if (Date.now() - parsed.data.lastUpdatedAt > DRAFT_TTL_MS) {
      clearDraft();
      return null;
    }

    const { lastUpdatedAt: _ts, ...values } = parsed.data;

    return values;
  } catch {
    clearDraft();
    return null;
  }
}

/**
 * Persists the current form values as a draft.
 * Should be called debounced (e.g. 500 ms) from the form's value-change effect.
 */
export function saveDraft(values: BugReportFormValues): void {
  try {
    globalThis.localStorage.setItem(
      draftKey(),
      JSON.stringify({ ...values, lastUpdatedAt: Date.now() }),
    );
  } catch {
    // Storage quota exceeded or storage disabled — silently ignore.
  }
}

/**
 * Removes the draft for the current user.
 */
export function clearDraft(): void {
  try {
    globalThis.localStorage.removeItem(draftKey());
  } catch {
    // ignore
  }
}
